import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ProList from '@ant-design/pro-list';
import ProTable from '@ant-design/pro-table';
import {
  Button,
  Tag,
  Tabs,
  Descriptions,
  Space,
  Typography,
  Row,
  Col,
  Popconfirm,
  Form,
  Input,
  Select,
  Radio,
  Card,
  DatePicker,
  Modal,
  Tooltip,
  message,
  Checkbox,
  Spin,
  Empty,
  Pagination,
  Avatar,
  Image,
} from 'antd';
import {
  QuestionCircleTwoTone,
  FileSearchOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';
import ClassList from '@/components/ClassList';
import UserPlanView from '@/components/UserPlanView';

import moment from 'moment';
import {
  getInfo,
  listRelatives,
  relativesSaveInfo,
  getTrack,
  saveTrack,
  relativesGetInfo,
  deleteTrack,
  delRel,
  customerReplies,
  palmerChangePhone,
  listCountryCodeList,
  getUserProfile,
  getGroupAnalysisData,
  getUserMemory,
  getUserMemoryData,
  getManagedAllGroupChatMembers,
  switchWechatBind,
} from '@/services/userManager';
import {
  saveRenewAgreementSuspended,
  queryRenewAgreementSuspendedList,
  invalidRenewAgreementSuspended,
} from '@/services/renewal';
import { userAgreementInfo, userAgreementUnSign } from '@/services/order';
import OverView from '@/pages/user/components/OverView';
import PathRecordView from '@/pages/user/components/PathRecordView';
import RightView from '@/pages/user/components/RightView';
import OrderRecordView from '@/pages/user/components/OrderRecordView';
import LittaVodRecordView from '@/pages/user/components/LittaVodRecordView';
import DelClassRecordView from '@/pages/user/components/DelClassRecordView';
import RelativeView from '@/pages/user/components/RelativeView';
import ChatListView from '@/pages/user/components/ChatListView';
import TouchRecordView from '@/pages/user/components/TouchRecordView';
import TrainingView from '@/pages/user/components/TrainingView';
import HealthHabitView from '@/pages/user/components/HealthHabitView';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

// 记忆体数据卡片：主态（key=16）与客态（key=17）两个 tab 共用，仅数据源不同
const MemoryMapCard = ({ map, loading }) => {
  const keys = Object.keys(map || {});
  return (
    <Card title="记忆体数据" headStyle={{ background: '#f0f7ff' }}>
      {keys.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#999' }}>
          {loading ? '正在加载...' : '暂无数据'}
        </div>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {keys.map((k) => {
            const list = Array.isArray(map[k]) ? map[k] : [];
            return (
              <Card key={k} size="small" title={k}>
                <Space
                  direction="vertical"
                  style={{ width: '100%' }}
                  size="middle"
                >
                  {list.length === 0 ? (
                    <div style={{ color: '#999' }}>暂无数据</div>
                  ) : (
                    list.map((item) => (
                      <div
                        key={item.id}
                        style={{ lineHeight: 1.8, color: '#333' }}
                      >
                        {item.content}
                      </div>
                    ))
                  )}
                </Space>
              </Card>
            );
          })}
        </Space>
      )}
    </Card>
  );
};

MemoryMapCard.propTypes = {
  map: PropTypes.object,
  loading: PropTypes.bool,
};

// 中期记忆卡片：「交互」与「活动」两处渲染结构完全一致，仅标题与数据源不同
const MidMemoryCard = ({ title, list }) => (
  <Card size="small" title={title}>
    {(() => {
      if (!Array.isArray(list) || list.length === 0) {
        return <div style={{ color: '#999' }}>暂无数据</div>;
      }
      return (
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          {list.map((item, idx) => {
            const start = item?.timePeriodStart;
            const end = item?.timePeriodEnd;
            const startFmt =
              start && moment(start).isValid()
                ? moment(start).format('YYYY-MM-DD')
                : start || '';
            const endFmt =
              end && moment(end).isValid()
                ? moment(end).format('YYYY-MM-DD')
                : end || '';
            let lines = [];
            let summaryStr = '';
            try {
              const parsed =
                typeof item?.abstractionSummary === 'string'
                  ? JSON.parse(item.abstractionSummary)
                  : item?.abstractionSummary;
              if (Array.isArray(parsed)) {
                lines = parsed;
              } else if (parsed) {
                summaryStr = String(parsed);
              }
            } catch {
              summaryStr = String(item?.abstractionSummary || '').trim();
            }
            return (
              <div key={idx} style={{ lineHeight: 1.6 }}>
                <Space wrap>
                  {startFmt ? <Tag color="gold">{startFmt}</Tag> : null}
                  {endFmt ? <Tag color="gold">{endFmt}</Tag> : null}
                  {item?.abstractionType ? (
                    <Tag color="blue">{item.abstractionType}</Tag>
                  ) : null}
                </Space>
                <div style={{ marginTop: 6 }}>
                  {lines.length ? (
                    <Space wrap style={{ width: '100%' }}>
                      {lines.map((s, k) => (
                        <Tag
                          color="magenta"
                          key={k}
                          style={{
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            maxWidth: '100%',
                          }}
                        >
                          {s}
                        </Tag>
                      ))}
                    </Space>
                  ) : summaryStr ? (
                    <div style={{ color: '#333' }}>{summaryStr}</div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </Space>
      );
    })()}
  </Card>
);

MidMemoryCard.propTypes = {
  title: PropTypes.string,
  list: PropTypes.array,
};

const record = (props) => {
  const {
    // eslint-disable-next-line react/prop-types
    location: { query },
  } = props;
  // 客户基础信息
  const [userInfo, setUserInfo] = useState();
  // 下拉亲属关系
  const [relatives, setRelatives] = useState([]);
  // 用户信息 questionData - addition
  // 跟进进度
  // eslint-disable-next-line no-unused-vars
  const [trackSteps, setTrackSteps] = useState([]);
  // 编辑记录
  const [customerRepliesList, setCustomerRepliesList] = useState([]);
  const [sportsWillingnessesList, setSportsWillingnessesList] = useState([]);
  // 亲属关系数组 {relationId: {}}
  const [relationInfo, setRelationInfo] = useState({});
  const [currentRelationInfo, setCurrentRelationInfo] = useState();
  // 当前tab用户
  const [currentRelationID, setCurrentRelationID] = useState(0);
  const [currentRelationPhone, setCurrentRelationPhone] = useState();
  /// 添加特征
  const [addFeatureForm] = Form.useForm();
  const [addFeatureModalVisible, setAddFeatureModalVisible] = useState(false);
  /// 添加跟进
  const [addFollowForm] = Form.useForm();
  const [addFollowModalVisible, setAddFollowModalVisible] = useState(false);
  /// 添加亲属
  const [addFamilyForm] = Form.useForm();
  const [addFamilyModalVisible, setAddFamilyModalVisible] = useState(false);
  // 跟进
  const followTableRef = useRef();
  const [editFollowRecord, setEditFollowRecord] = useState();
  const [currentTabsKey, setCurrentTabsKey] = useState('0');
  // 关系
  const relativeViewRef = useRef();
  // 群数据分析状态
  const [groupAnalysisData, setGroupAnalysisData] = useState(null);
  const [groupAnalysisLoading, setGroupAnalysisLoading] = useState(false);
  // 群数据分析分页状态
  const [groupAnalysisCurrentPage, setGroupAnalysisCurrentPage] = useState(1);
  const [groupAnalysisPageSize, setGroupAnalysisPageSize] = useState(1);
  // 健康习惯模块集成由 HealthHabitView 组件自处理
  function disabledDate(current) {
    return current && current > moment().subtract(1, 'days').endOf('day');
  }

  // 获取群数据分析数据
  const fetchGroupAnalysisData = async (
    page = groupAnalysisCurrentPage,
    pageSize = groupAnalysisPageSize,
  ) => {
    setGroupAnalysisLoading(true);
    try {
      // 这里应该调用实际的API接口
      const res = await getGroupAnalysisData({
        userId: query.id,
        page: {
          currentPage: page,
          pageSize: pageSize,
        },
      });
      if (res.code == '00000') {
        // 模拟分页数据

        setGroupAnalysisData(res);
        // 更新分页状态
        if (res.page) {
          setGroupAnalysisCurrentPage(res.page.currentPage);
          setGroupAnalysisPageSize(res.page.pageSize);
        }
      }
    } catch (error) {
      console.error('获取群数据分析失败:', error);
      message.error('获取群数据分析失败');
    } finally {
      setGroupAnalysisLoading(false);
    }
  };
  const disabledDateTime = () => ({
    disabledHours: () => [0, 1, 2, 3, 4, 5, 6, 7, 23],
  });
  const classListRef = useRef();
  // tabs
  const [tabSource, setTabSource] = useState([
    {
      relationName: '客户',
      relationId: 0,
    },
  ]);

  // 更换手机号
  const [countryCodes, setCountryCodes] = useState([]);
  const [changePhoneForm] = Form.useForm();
  const [changePhoneModalVisible, setChangePhoneModalVisible] = useState(false);
  const [memoryLoading, setMemoryLoading] = useState(false);
  const [userMemoryMapUser, setUserMemoryMapUser] = useState();
  const [userMemoryMapCoach, setUserMemoryMapCoach] = useState();
  const [memorySourceLoading, setMemorySourceLoading] = useState(false);
  const [memorySource, setMemorySource] = useState();

  // 停卡
  const [stopModalVisible, setStopModalVisible] = useState(false);
  const [stopForm] = Form.useForm();
  const operateRef = useRef();
  const [suspendedType, setSuspendedType] = useState();

  const [autoSignVisible, setAutoSignVisible] = useState(false);
  const [wxBindModalVisible, setWxBindModalVisible] = useState(false);
  const [loadings, setLoadings] = useState([]);
  const [signList, setSignList] = useState([]);
  const step = useRef(0);

  // 新增：微信绑定关系搜索相关状态
  const [wxNicknameSearch, setWxNicknameSearch] = useState('');
  const wxBindTableRef = useRef();
  // 在微信昵称后添加搜索按钮
  const wxBindAction = () => {
    setWxBindModalVisible(true);
    setWxNicknameSearch(''); // 打开时清空搜索条件
  };

  // 新增：存储getUserProfile获取的用户详细信息
  const [userProfileData, setUserProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // 钩子函数
  useEffect(() => {
    setCurrentTabsKey(query.tabKey);

    // 获取国家代码
    queryCountryCodes();

    // 获取亲属关系
    queryCustomInfo();
    // 亲属下拉列表
    queryRelative();
    // 跟进
    queryCustomerReplies();
    // 在组件加载时获取用户详细信息
    fetchUserProfile(query.id);
  }, []);

  /// 亲属关系下拉列表
  const queryCountryCodes = async () => {
    const { data, code, msg } = await listCountryCodeList({
      requestData: {},
    });
    if (code !== '00000') {
      message.error(msg);
    } else {
      setCountryCodes(data);
    }
  };

  // 获取回复。意愿列表
  const queryCustomerReplies = async () => {
    customerReplies({
      requestData: {},
    }).then(({ code, data, msg }) => {
      if (code === '00000') {
        setCustomerRepliesList(data.customerReplies || []);
        setSportsWillingnessesList(data.sportsWillingnesses || []);
      } else {
        message.error(msg);
      }
    });
  };

  // 获取亲属关系
  const queryCustomInfo = async () => {
    getInfo({
      requestData: { userId: query.id },
    }).then(({ code, data, msg }) => {
      if (code === '00000') {
        setUserInfo(data);
        const tempTab = [
          {
            relationName: '客户',
            relationId: 0,
          },
        ];
        (data?.relatives || []).map((e) => tempTab.push(e));
        setTabSource(tempTab);
        // 初始加载时，也为默认选中的主用户Tab获取其详情，以便头部展示
        updateRelationInfo(0);
      } else {
        message.error(msg);
      }
    });
  };

  /// 亲属关系下拉列表
  const queryRelative = async () => {
    const { data, code, msg } = await listRelatives({
      requestData: {},
    });
    if (code !== '00000') {
      message.error(msg);
    } else {
      setRelatives(data);
    }
  };

  const callback = (key) => {
    setCurrentRelationID(key);
    // 无论是否大于0，都调用 updateRelationInfo 来获取当前选中 Tab 的详情信息
    updateRelationInfo(key);
    if (key > 0) {
      setCurrentTabsKey('1');
    } else {
      setCurrentTabsKey('0');
    }
  };

  function onTabChange(key) {
    setCurrentTabsKey(key);
    // 当切换到基础资料tab (key === '1') 且当前选中relation的信息已加载时，调用fetchUserProfile
    if (key === '1' && currentRelationInfo?.userId !== undefined) {
      fetchUserProfile(currentRelationInfo.userId);
    }
  }

  const updateRelationInfo = (relationIdToFetch) => {
    relativesGetInfo({
      requestData: {
        userId: query.id,
        relationId: relationIdToFetch,
      },
    }).then(({ data, code, msg }) => {
      if (code === '00000') {
        // 刷新亲属tab
        const temp = relationInfo;
        temp[relationIdToFetch] = data;
        setRelationInfo(temp);
        // 用户本人
        // warning !!!  currentRelationID 由于服务端返回 0/ '0' ,勿修改为 ===
        // 当前的record
        setCurrentRelationInfo(data);
        setCurrentRelationPhone(data.phone);
        // 获取当前选中用户的详细资料（基础资料tab使用）
        if (data?.userId !== undefined) {
          fetchUserProfile(data.userId);
        }
      } else {
        message.error(msg);
      }
    });
  };

  // 获取用户详细信息函数
  const fetchUserProfile = (userId, relationId = 0) => {
    setProfileLoading(true);
    getUserProfile({
      requestData: {
        userId,
        relationId, // 添加relationId参数，支持获取亲属信息
      },
    })
      .then(({ code, data }) => {
        if (code === '00000') {
          setUserProfileData(data);
        } else {
          message.error('获取用户详情失败');
          setUserProfileData(null); // 清空旧数据
        }
        setProfileLoading(false);
      })
      .catch(() => {
        message.error('获取用户详情失败');
        setUserProfileData(null); // 清空旧数据
        setProfileLoading(false);
      });
  };

  // 计算BMI - 沿用用户列表页的逻辑
  const calculateBMI = (height, weight) => {
    if (!height || !weight) return '-';
    const heightInMeters = height / 100;
    const bmi = (weight / (heightInMeters * heightInMeters)).toFixed(1);
    return bmi;
  };

  const addFeatureAction = () => {
    if (currentRelationID == 0) {
      addFeatureForm.setFieldsValue({
        feature: userInfo?.feature,
        requirement: userInfo?.requirement,
      });
    } else {
      addFeatureForm.setFieldsValue({
        feature: currentRelationInfo?.feature,
        requirement: currentRelationInfo?.requirement,
      });
    }
    setAddFeatureModalVisible(true);
  };
  const addFeatureModalOkAction = () => {
    addFeatureForm.validateFields().then((values) => {
      relativesSaveInfo({
        requestData: {
          userId: query.id,
          relationId: currentRelationID,
          id: currentRelationInfo?.id,
          phone: currentRelationInfo?.phone,
          ...values,
        },
      }).then(({ code, msg }) => {
        if (code === '00000') {
          message.success('添加成功！');
          // 刷新亲属tab
          updateRelationInfo(currentRelationID);
        } else {
          message.error(msg);
        }
        addFeatureForm.resetFields();
        setAddFeatureModalVisible(false);
      });
    });
  };
  const addFeatureModalCancelAction = () => {
    addFeatureForm.resetFields();
    setAddFeatureModalVisible(false);
  };

  const addFeatureModalView = () => {
    return (
      <Modal
        title={currentRelationID === 0 ? '添加客户特征' : '添加亲属特征'}
        visible={addFeatureModalVisible}
        onCancel={() => addFeatureModalCancelAction(false)}
        footer={[]}
        width={800}
      >
        <Form
          form={addFeatureForm}
          name="removeClassForm"
          className="form"
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 20 }}
        >
          <Form.Item name="feature" label="客户特征">
            <TextArea
              showCount
              maxLength={2000}
              autoSize={{ minRows: 5, maxRows: 6 }}
              placeholder="比如购买力、特征等备注信息，2000个字符以内"
            />
          </Form.Item>

          <Form.Item
            name="requirement"
            label="客户诉求"
            placeholder="请输入购买体验课的原因，首次沟通情况，2000个字符以内"
          >
            <TextArea
              showCount
              autoSize={{ minRows: 5, maxRows: 6 }}
              maxLength={2000}
            />
          </Form.Item>

          <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
            <Button type="primary" onClick={addFeatureModalOkAction}>
              提交
            </Button>
            <Button
              htmlType="button"
              style={{ margin: '0 16px' }}
              onClick={addFeatureModalCancelAction}
            >
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    );
  };

  const changePhoneAction = () => {
    setChangePhoneModalVisible(true);
  };
  const changePhoneModalOkAction = () => {
    changePhoneForm.validateFields().then((values) => {
      palmerChangePhone({
        requestData: {
          userId: query.id,
          phone: values.newPhone,
          countryCode: values.countryCode,
        },
      }).then(({ code, msg, data }) => {
        if (code === '00000') {
          message.success('更换成功！');
          queryCustomInfo();
          fetchUserProfile(query.id);
          window.history.replaceState(
            {},
            0,
            '/litta-palmer/user/record?id=' + query.id + '&phone=' + data.phone,
          );
        } else {
          message.error(msg);
        }
        setChangePhoneModalVisible(false);
        changePhoneForm.resetFields();
      });
    });
  };
  const changePhoneModalCancelAction = () => {
    changePhoneForm.resetFields();
    setChangePhoneModalVisible(false);
  };

  const addFollowAction = () => {
    setAddFollowModalVisible(true);
  };

  const getTrackName = (step) => {
    for (let i = 0; i < trackSteps.length; i++) {
      const track = trackSteps[i];
      if (parseInt(step) === parseInt(track.type)) {
        return track.name;
      }
    }
  };
  const addFollowModalOkAction = () => {
    addFollowForm.validateFields().then((values) => {
      const param = {
        trackType: 2,
        userId: query.id,
        ...values,
        resentTrackTime: moment(values.resentTrackTime).format(
          'YYYY-MM-DD HH:mm',
        ),
        nextTrackTime: values.nextTrackTime
          ? moment(values.nextTrackTime).format('YYYY-MM-DD HH:mm')
          : undefined,
      };

      if (editFollowRecord) {
        param.id = editFollowRecord.id;
      }

      saveTrack({
        requestData: param,
      }).then(({ code, msg }) => {
        if (code === '00000') {
          message.success('添加成功！');
          // 刷新跟进
          followTableRef.current?.reload();
        } else {
          message.error(msg);
        }
        // 刷新跟进
        followTableRef.current?.reload();
        setAddFollowModalVisible(false);
        addFollowForm.resetFields();
        setEditFollowRecord(undefined);
      });
    });
  };
  const addFollowModalCancelAction = () => {
    addFollowForm.resetFields();
    setEditFollowRecord(undefined);
    setAddFollowModalVisible(false);
  };

  const addFollowModalView = () => {
    return (
      <Modal
        title={editFollowRecord ? '编辑跟进' : '添加跟进'}
        visible={addFollowModalVisible}
        onCancel={() => addFollowModalCancelAction(false)}
        footer={[]}
        width={800}
      >
        <Form
          form={addFollowForm}
          name="removeClassForm"
          className="form"
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 18 }}
        >
          <Form.Item
            name="resentTrackTime"
            label="最近跟进时间"
            initialValue={moment()}
            rules={[
              {
                required: true,
                message: '请选择最近跟进时间!',
              },
            ]}
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              disabledDate={disabledDate}
            />
          </Form.Item>

          <Form.Item
            name="nextTrackTime"
            label="下次跟进时间"
            initialValue={moment().add(2, 'days')}
          >
            <DatePicker
              showNow={false}
              disabledTime={disabledDateTime}
              showTime
              minuteStep={10}
              format="YYYY-MM-DD HH:mm"
            />
          </Form.Item>

          <Form.Item
            name="customerReply"
            label="客户回复"
            rules={[
              {
                required: true,
                message: '请选择客户回复!',
              },
            ]}
          >
            <Radio.Group buttonStyle="solid">
              {customerRepliesList.map((item) => {
                return (
                  <Radio.Button
                    key={item?.customerReply}
                    value={item?.customerReply}
                  >
                    {item?.desc}
                  </Radio.Button>
                );
              })}
            </Radio.Group>
          </Form.Item>

          <Form.Item name="trackInfo" label="其他备注说明">
            <TextArea
              showCount
              placeholder="其他备注说明，500个字符以内选填"
              maxLength={500}
            />
          </Form.Item>

          <Form.Item
            name="sportsWillingness"
            label="运动意愿"
            rules={[
              {
                required: true,
                message: '请选择客户回复!',
              },
            ]}
          >
            <Radio.Group buttonStyle="solid">
              {sportsWillingnessesList.map((item) => {
                return (
                  <Radio.Button
                    key={item?.sportsWillingness}
                    value={item?.sportsWillingness}
                  >
                    {item?.desc}
                  </Radio.Button>
                );
              })}
            </Radio.Group>
          </Form.Item>

          <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
            <Button type="primary" onClick={addFollowModalOkAction}>
              提交
            </Button>
            <Button
              htmlType="button"
              style={{ margin: '0 16px' }}
              onClick={addFollowModalCancelAction}
            >
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    );
  };

  const deleteRelation = () => {
    delRel({
      requestData: {
        userId: query.id,
        relationId: currentRelationID,
      },
    }).then(({ code, msg }) => {
      if (code === '00000') {
        message.success('删除成功！');
        // 删除本地对应数据
        setRelationInfo({});
        // 刷新亲属tab
        setCurrentRelationID(0);
        setTabSource([]);
        queryCustomInfo();
      } else {
        message.error(msg);
      }
    });
  };

  const addFamilyAction = () => {
    setAddFamilyModalVisible(true);
  };
  const addFamilyModalOkAction = () => {
    addFamilyForm.validateFields().then((values) => {
      relativesSaveInfo({
        requestData: {
          userId: query.id,
          ...values,
        },
      }).then(({ code, msg }) => {
        if (code === '00000') {
          message.success('添加成功！');
          // 刷新亲属tab
          // 删除本地对应数据
          setRelationInfo({});
          // 刷新亲属tab
          setCurrentRelationID(0);
          setTabSource([]);
          queryCustomInfo();
        } else {
          message.error(msg);
        }
        setAddFamilyModalVisible(false);
        addFamilyForm.resetFields();
      });
    });
  };
  const addFamilyModalCancelAction = () => {
    addFamilyForm.resetFields();
    setAddFamilyModalVisible(false);
  };

  const stopColumns = [
    {
      title: '操作时间',
      dataIndex: 'mtimeInfo',
      key: 'mtimeInfo',
    },
    {
      title: '操作人',
      dataIndex: 'operatorName',
      key: 'operatorName',
    },
    {
      title: '续费停卡时间',
      dataIndex: 'startTimeInfo',
      key: 'startTimeInfo',
      render: (_, record, index, action) => {
        return record?.startTimeInfo + '至' + record?.expireTimeInfo;
      },
    },
    {
      title: '类型',
      dataIndex: 'startTimeInfo',
      key: 'startTimeInfo',
      render: (_, record, index, action) => {
        return (
          <div>{record.suspendedType === 2 ? '权益停卡' : '续费停扣'}</div>
        );
      },
    },
    {
      title: '备注说明',
      dataIndex: 'suspendedRemark',
      key: 'suspendedRemark',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      valueType: 'select',
      valueEnum: {
        1: {
          text: '停卡中',
        },
        2: {
          text: '已失效',
        },
      },
    },
    {
      title: '操作',
      valueType: 'option',
      key: 'option',
      fixed: 'right',
      width: '100',
      render: (text, record) => {
        if (record.status === 1) {
          return (
            <a
              key="newFollow"
              onClick={() => invalidRenewAgreementSuspendedAction(record)}
            >
              失效
            </a>
          );
        }
        return '-';
      },
    },
  ];

  const wxBindColumns = [
    {
      title: '群名称',
      dataIndex: 'groupChatName',
      key: 'groupChatName',
    },
    {
      title: '微信昵称',
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => (
        <Space>
          {record.avatar ? <Avatar src={record.avatar} size="small" /> : null}
          <span>{record.name || '-'}</span>
        </Space>
      ),
    },
    {
      title: '群内昵称',
      dataIndex: 'groupNickname',
      key: 'groupNickname',
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: '微信UnionId',
      dataIndex: 'unionId',
      key: 'unionId',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => {
        // 如果当前用户手机号和列表中的手机号相同，则不显示操作按钮
        if (currentRelationInfo?.phone === record.phone) {
          return '-';
        }
        return (
          <Button
            type="link"
            size="small"
            onClick={() => handleSwitchBind(record)}
          >
            切换绑定
          </Button>
        );
      },
    },
  ];
  // 停卡modal
  const stopModalView = () => {
    return (
      <Modal
        title="签约停卡"
        visible={stopModalVisible}
        onCancel={() => {
          stopForm.resetFields();
          setStopModalVisible(false);
        }}
        footer={[]}
        width={800}
      >
        <Form
          form={stopForm}
          name="removeClassForm"
          className="stopForm"
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 20 }}
        >
          <Form.Item
            name="stop"
            label="停卡类型"
            rules={[
              {
                required: true,
                message: '请选择停卡类型!',
              },
            ]}
          >
            <Radio.Group
              onChange={(e) => {
                suspendedTypeChange(e);
              }}
            >
              <Radio value={2}>
                权益停卡
                {resultTip('将用户权益延期，在停卡期间，用户无法使用权益')}
              </Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="time"
            label="停卡日期"
            tooltip="用户签约了自动续费，在该期限内权益到期的话，不进行触发扣费"
            rules={[
              {
                required: true,
                message: '请选择停卡日期!',
              },
            ]}
          >
            <RangePicker
              format="YYYY-MM-DD"
              style={{ width: '100%' }}
              disabledDate={disabledStopDate}
            />
          </Form.Item>

          <Form.Item name="suspendedRemark" label="备注说明">
            <TextArea
              showCount
              placeholder="备注说明，256个字符以内选填 "
              maxLength={256}
            />
          </Form.Item>

          <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
            <Button type="primary" onClick={onFormOkAction}>
              保存
            </Button>
            <Button
              htmlType="button"
              style={{ margin: '0 100px' }}
              onClick={onFormCancelAction}
            >
              取消
            </Button>
          </Form.Item>
        </Form>

        <Paragraph>
          <blockquote style={{ borderColor: 'lightBlue' }}>操作记录</blockquote>
        </Paragraph>
        <ProTable
          columns={stopColumns}
          actionRef={operateRef}
          request={async (params) => {
            const { data, page, code } = await queryRenewAgreementSuspendedList(
              {
                // TODO: 传啥参数
                requestData: {
                  ...params,
                  // agreementId: editRecord.id,
                  userId: query.id,
                },
                page: {
                  currentPage: params.current,
                  pageSize: params.pageSize,
                },
              },
            );
            if (code !== '00000') {
              return {
                success: false,
              };
            }
            return {
              data: data,
              success: true,
              total: page.count,
            };
          }}
          editable={{
            type: 'multiple',
          }}
          columnsState={{
            persistenceKey: 'pro-table-singe-demos',
            persistenceType: 'localStorage',
          }}
          rowKey={(record) => record.id}
          search={false}
          scroll={{ x: 'max-content' }}
          pagination={{
            defaultPageSize: 10,
          }}
          dateFormatter="string"
          toolBarRender={false}
        />
      </Modal>
    );
  };

  function autoSignModalView() {
    return (
      <Modal
        title="自动续费管理"
        visible={autoSignVisible}
        onCancel={() => {
          setAutoSignVisible(false);
        }}
        footer={[]}
        width={800}
      >
        <Space style={{ display: 'flex', alignItems: 'flex-start' }}>
          <Space>
            <Tooltip title={'勾选后将把用户自动续费进行解约'}>
              <QuestionCircleTwoTone />
            </Tooltip>
            <span style={{ wordBreak: 'keep-all' }}> 主动解约续费</span>
          </Space>
          <Space wrap>
            {(signList || []).map((item, index) => {
              const { planName, id, allowUnSign } = item;
              return (
                <span key={index}>
                  {planName}
                  <Button
                    disabled={!allowUnSign}
                    loading={loadings[index]}
                    onClick={() => {
                      onCancelSign(index, id);
                    }}
                    type="primary"
                    size="small"
                    value={1}
                  >
                    {allowUnSign ? '解约' : '已解约'}
                  </Button>
                </span>
              );
            })}
            {signList?.length === 0 && '无签约信息'}
          </Space>{' '}
        </Space>
      </Modal>
    );
  }

  function wxBindModalView() {
    return (
      <Modal
        title="切换微信绑定关系"
        visible={wxBindModalVisible}
        onCancel={() => {
          setWxBindModalVisible(false);
          setWxNicknameSearch(''); // 关闭时清空搜索条件
        }}
        footer={[]}
        width={1200}
      >
        {/* 搜索区域 */}
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Input
              placeholder="请输入微信昵称"
              value={wxNicknameSearch}
              onChange={(e) => setWxNicknameSearch(e.target.value)}
              style={{ width: 200 }}
              allowClear
              onPressEnter={() => {}}
            />
            <Button
              type="primary"
              onClick={() => {
                // 触发表格重新加载
                if (wxBindTableRef.current) {
                  wxBindTableRef.current.reload();
                }
              }}
            >
              搜索
            </Button>
            <Button
              onClick={() => {
                setWxNicknameSearch('');
                if (wxBindTableRef.current) {
                  wxBindTableRef.current.reload();
                }
              }}
            >
              重置
            </Button>
          </Space>
        </div>
        <ProTable
          actionRef={wxBindTableRef} // 添加actionRef关联表格引用
          columns={wxBindColumns}
          request={async (params) => {
            const { current, pageSize, ...rest } = params;
            const { data, page, code, msg } =
              await getManagedAllGroupChatMembers({
                requestData: {
                  wxNickName: wxNicknameSearch, // 添加微信昵称搜索参数
                  ...rest,
                },
                page: {
                  currentPage: current,
                  pageSize,
                },
              });
            if (code !== '00000') {
              if (msg) {
                message.error(msg);
              }
              return {
                success: false,
              };
            }
            return {
              data: data || [],
              success: true,
              total: page?.count || 0,
            };
          }}
          rowKey={(record) => record.id}
          search={false}
          pagination={{
            defaultPageSize: 10,
          }}
          params={{ wxNickName: wxNicknameSearch }} // 添加搜索参数到表格参数中
        />
      </Modal>
    );
  }

  // 处理切换微信绑定关系
  const handleSwitchBind = (record) => {
    const { unionId, name } = record;
    // 这里调用后台接口，需要根据实际接口定义进行调用
    switchWechatBind({
      requestData: {
        targetUserId: currentRelationInfo?.userId,
        unionId: unionId,
        wxNickName: name,
      },
    }).then(({ data, code, msg }) => {
      if (code === '00000') {
        if (data?.result) {
          message.success('切换绑定成功');
          // 切换绑定成功后刷新页面数据
          queryCustomInfo();
        } else {
          message.error('切换绑定失败');
        }
      } else {
        message.error(msg);
      }
      // 切换绑定成功后关闭弹出框
      setWxBindModalVisible(false);
      // 清空搜索条件
      setWxNicknameSearch('');
    });
  };

  function onCancelSign(index, id) {
    setLoadings((prevLoadings) => {
      const newLoadings = [...prevLoadings];
      newLoadings[index] = true;
      return newLoadings;
    });

    userAgreementUnSign({ requestData: { renewAgreementId: id } })
      .then(({ code, msg }) => {
        setLoadings((prevLoadings) => {
          const newLoadings = [...prevLoadings];
          newLoadings[index] = false;
          return newLoadings;
        });

        if (code === '00000') {
          message.success('解约成功');
          queryCustomInfo();
          getOrderSign(query.id);

          step.current++;
        } else {
          message.error(msg);
        }
      })
      .catch(() => {
        setLoadings((prevLoadings) => {
          const newLoadings = [...prevLoadings];
          newLoadings[index] = false;
          return newLoadings;
        });
      });
  }

  function getOrderSign(userId) {
    userAgreementInfo({ requestData: { userId: userId } })
      .then(({ data }) => {
        setSignList([...data]);
      })
      .catch(() => {});
  }
  function onFormCancelAction() {
    stopForm.resetFields();
    setStopModalVisible(false);
  }
  const stopAction = () => {
    setStopModalVisible(true);
  };
  const disabledStopDate = (time) => {
    if (!time) {
      return false;
    } else {
      // 大于当前日期不能选 time > moment()
      // 小于当前日期不能选 time < moment().subtract(1, "days")
      // 只能选前7后7 time < moment().subtract(7, "days") || time > moment().add(7, 'd')
      // return time < moment().subtract(7, "days") || time > moment().add(7, 'd')
      return time < moment() || time > moment().add(365, 'd');
    }
  };
  const invalidRenewAgreementSuspendedAction = (record) => {
    invalidRenewAgreementSuspended({
      requestData: {
        ...record,
        status: 2,
      },
    }).then(({ code, msg, data }) => {
      if (code === '00000') {
        message.success(msg);
        operateRef.current.reload();
      } else {
        message.error(msg);
      }
    });
  };
  function suspendedTypeChange(e) {
    setSuspendedType(e.target.value);
  }
  function onFormOkAction() {
    stopForm.validateFields().then((values) => {
      saveRenewAgreementSuspended({
        requestData: {
          userId: query.id,
          suspendedType: suspendedType,
          ...values,
          startTimeInfo: values.time[0].format('YYYY-MM-DD'),
          expireTimeInfo: values.time[1].format('YYYY-MM-DD'),
          time: undefined,
        },
      }).then(({ code, msg }) => {
        if (code === '00000') {
          message.success(msg);
          stopForm.resetFields();
          operateRef.current.reload();
        } else {
          message.error(msg);
        }
      });
    });
  }
  function resultTip(tip) {
    return (
      <Tooltip title={tip}>
        <QuestionCircleTwoTone style={{ marginLeft: 5 }} />
      </Tooltip>
    );
  }
  const addFamilyModalView = () => {
    return (
      <Modal
        title="添加亲属"
        visible={addFamilyModalVisible}
        onCancel={() => addFamilyModalCancelAction(false)}
        footer={[]}
      >
        <Form form={addFamilyForm} name="removeClassForm" className="form">
          <Form.Item
            name="relationId"
            label="称谓关系"
            rules={[
              {
                required: true,
                message: '请选择客户称谓关系!',
              },
            ]}
          >
            <Select placeholder="请选择">
              {relatives.map((item) => {
                return (
                  <Select.Option value={item.type} key={item.type}>
                    {item.name}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item
            name="countryCode"
            label="国家代码"
            initialValue={86}
            rules={[
              {
                required: true,
                message: '请选择国家',
              },
            ]}
          >
            <Select
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              filterSort={(optionA, optionB) =>
                optionA.children
                  .toLowerCase()
                  .localeCompare(optionB.children.toLowerCase())
              }
              allowClear
              placeholder="请选择国家"
              defaultValue={{
                value: 86,
                label: '中国大陆',
              }}
            >
              {countryCodes.map((item) => {
                return (
                  <Select.Option value={item.phoneCode} key={item.phoneCode}>
                    {`${item.countryName} (+${item.phoneCode})`}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
          <Form.Item
            name="phone"
            label="手机号码"
            rules={[
              {
                required: true,
                message: '请输入手机号码',
                // eslint-disable-next-line prefer-regex-literals
                pattern: new RegExp(/^[0-9]\d*$/, 'g'),
              },
            ]}
          >
            <Input placeholder="11位数字" maxLength={11} />
          </Form.Item>
          {/* 共享权益前端固定写死 */}
          <Form.Item
            name="right"
            label="共享权益"
            initialValue={['1', '2', '3']}
            rules={[
              {
                required: true,
                message: '请选择共享权益',
              },
            ]}
          >
            <Checkbox.Group disabled="true">
              <Checkbox value="1">AI课权益</Checkbox>
              <Checkbox value="2">真人私教权益</Checkbox>
              <Checkbox value="3">LITTA视频</Checkbox>
            </Checkbox.Group>
          </Form.Item>

          <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
            <Button type="primary" onClick={addFamilyModalOkAction}>
              提交
            </Button>
            <Button
              htmlType="button"
              style={{ margin: '0 16px' }}
              onClick={addFamilyModalCancelAction}
            >
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    );
  };

  const changePhoneModalView = () => {
    return (
      <Modal
        title="更换手机号"
        visible={changePhoneModalVisible}
        onCancel={() => changePhoneModalCancelAction(false)}
        footer={[]}
      >
        <Form form={changePhoneForm} name="removeClassForm" className="form">
          <Form.Item
            name="countryCode"
            label="国家代码"
            initialValue={86}
            rules={[
              {
                required: true,
                message: '请选择国家',
              },
            ]}
          >
            <Select
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              filterSort={(optionA, optionB) =>
                optionA.children
                  .toLowerCase()
                  .localeCompare(optionB.children.toLowerCase())
              }
              allowClear
              placeholder="请选择国家"
              defaultValue={{
                value: 86,
                label: '中国大陆',
              }}
            >
              {countryCodes.map((item) => {
                return (
                  <Select.Option value={item.phoneCode} key={item.phoneCode}>
                    {`${item.countryName} (+${item.phoneCode})`}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item
            name="newPhone"
            label="新手机号"
            rules={[
              {
                required: true,
                message: '请输入手机号码',
                // eslint-disable-next-line prefer-regex-literals
                pattern: new RegExp(/^[0-9]\d*$/, 'g'),
              },
            ]}
          >
            <Input placeholder="请输入手机号码" />
          </Form.Item>

          <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
            <Button type="primary" onClick={changePhoneModalOkAction}>
              提交
            </Button>
            <Button
              htmlType="button"
              style={{ margin: '0 16px' }}
              onClick={changePhoneModalCancelAction}
            >
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    );
  };

  const userInfoContent = (info) => {
    return (
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 30,
          }}
        >
          <span>
            注册手机：
            <Space>
              {info?.countryCode ? '+' + info?.countryCode : ''}
              {info?.phone ?? '-'}
              {info?.gender === 1 || info?.gender === 2 ? (
                info?.gender === 2 ? (
                  <Tag>女</Tag>
                ) : (
                  <Tag>男</Tag>
                )
              ) : (
                ''
              )}
              {info?.age ? <Tag>{info?.age}</Tag> : ''}
            </Space>
          </span>
          <span>
            微信昵称：{info?.wxNickname ?? '-'}
            <Button
              type="link"
              icon={<FileSearchOutlined />}
              onClick={wxBindAction}
              style={{ marginLeft: 8 }}
            />
          </span>
          <span>平台昵称：{info?.nickname ?? '-'}</span>
          <span>
            自动续费状态：
            {info?.autoRenew ?? '未签约'}
            {/* 只有主用户才显示自动续费管理按钮 */}
            {currentRelationID === 0 && (
              <Button
                type="link"
                icon={<FileSearchOutlined />}
                onClick={() => {
                  setSignList([]);
                  getOrderSign(query.id);
                  setAutoSignVisible(true);
                }}
                style={{
                  marginLeft: 'auto',
                }}
              />
            )}
          </span>
        </div>
        {info?.feature ? (
          <div style={{ marginTop: 20 }}>客户特征：{info?.feature}</div>
        ) : (
          ''
        )}
        {info?.requirement ? (
          <div style={{ marginTop: 20 }}>客户诉求：{info?.requirement}</div>
        ) : (
          ''
        )}
      </div>
    );
  };

  // 本人-客户信息
  const baseContent = () => {
    return (
      <div style={{ marginBottom: 30 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Title level={5}>客户信息</Title>
          <Space>
            <Button type="primary" onClick={() => changePhoneAction()}>
              更换手机号
            </Button>
            <Button type="primary" onClick={() => stopAction()}>
              停卡
            </Button>
            <Button type="primary" onClick={() => addFeatureAction()}>
              客户特征
            </Button>
            <Button type="primary" onClick={() => addFamilyAction()}>
              添加家庭副卡
            </Button>
            <Button type="primary" onClick={() => addFollowAction()}>
              添加跟进
            </Button>
          </Space>
        </div>
        {userInfoContent(currentRelationInfo)}
      </div>
    );
  };

  // 亲属信息 (顶部区域)
  const baseRelationContent = () => {
    return (
      <div style={{ marginBottom: 30 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Title level={5}>客户信息</Title>
          <Space>
            <Button type="primary" onClick={() => addFeatureAction()}>
              亲属特征
            </Button>
            <Popconfirm title="确定删除吗？" onConfirm={() => deleteRelation()}>
              <Button type="primary">删除亲属</Button>
            </Popconfirm>
          </Space>
        </div>
        {userInfoContent(currentRelationInfo)}
      </div>
    );
  };

  const editFollow = (record) => {
    setEditFollowRecord(record);

    addFollowForm.setFieldsValue({
      ...record,
      resentTrackTime: record?.resentTrackTime
        ? moment(record?.resentTrackTime)
        : undefined,
      nextTrackTime: record?.nextTrackTime
        ? moment(record?.nextTrackTime)
        : undefined,
    });

    setAddFollowModalVisible(true);
  };
  const deleteFollowContent = (record) => {
    deleteTrack({
      requestData: {
        userId: query.id,
        id: record.id,
      },
    }).then(({ data, code, msg }) => {
      if (code === '00000') {
        followTableRef.current.reload();
      } else {
        message.error(msg);
      }
    });
  };
  const continueContent = (record) => {
    return (
      <div>
        <Paragraph>
          <ul>
            <li>
              <Row wrap={false}>
                <Col flex="none">
                  <Space size={'middle'}>
                    {record?.trackType === 2 ? (
                      <Tag color={'#87bb88'}>服务记录</Tag>
                    ) : (
                      ''
                    )}
                    <label>{record?.resentTrackTime}</label>
                    <label> 创建人：{record?.creatorName}</label>
                    <label> 客户阶段：{getTrackName(record?.step)}</label>
                    <label> 下次跟进时间：{record?.nextTrackTime}</label>
                  </Space>
                </Col>
                <Col flex="auto" style={{ textAlign: 'right' }}>
                  <Button type="link" onClick={() => editFollow(record)}>
                    编辑
                  </Button>
                  <Popconfirm
                    title="确定删除吗？"
                    onConfirm={() => deleteFollowContent(record)}
                  >
                    <Button type="link" danger>
                      删除
                    </Button>
                  </Popconfirm>
                </Col>
              </Row>
            </li>
          </ul>
        </Paragraph>
        <Paragraph>
          <blockquote>跟进内容</blockquote>
          {record.customerReplyDesc ? (
            <Tag color="purple">{record.customerReplyDesc}</Tag>
          ) : (
            ''
          )}
          {record.customerReplyDesc ? (
            <Tag color="purple">意愿度：{record.sportsWillingnessDesc}</Tag>
          ) : (
            ''
          )}
          <pre>{record?.trackInfo}</pre>
        </Paragraph>
        <br />
      </div>
    );
  };
  const continueList = () => {
    return (
      <ProList
        toolBarRender={false}
        search={false}
        rowKey="id"
        actionRef={followTableRef}
        itemLayout="vertical"
        headerTitle="基础列表"
        request={async (params) => {
          const { data, page, code } = await getTrack({
            requestData: {
              userId: query.id,
            },
            page: {
              currentPage: params.current,
              pageSize: params.pageSize,
            },
          });
          if (code !== '00000') {
            return {
              success: false,
            };
          }
          return {
            data: data,
            success: true,
            total: page.count,
          };
        }}
        pagination={{
          defaultPageSize: 10,
        }}
        showActions="hover"
        metas={{
          content: {
            render: (text, record) => {
              return continueContent(record);
            },
          },
        }}
      />
    );
  };

  // 渲染用户详细信息
  const renderUserProfileDetails = () => {
    if (profileLoading) {
      return (
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <Spin size="large" />
        </div>
      );
    }

    if (!userProfileData) {
      return (
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <p>暂无用户详细数据</p>
        </div>
      );
    }

    const {
      userId,
      gender,
      age,
      height,
      weight,
      bmi,
      bmr,
      geoInfo,
      questionnaireAnswers,
    } = userProfileData;

    // 合并基础信息和BMI、基础代谢等内容
    return (
      <>
        <Card title="基础信息" style={{ marginBottom: 16 }}>
          <Descriptions column={{ xxl: 6, xl: 6, lg: 6, md: 4, sm: 2, xs: 2 }}>
            <Descriptions.Item label="用户ID" span={2}>
              <Typography.Text copyable>{userId || '-'}</Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="性别" span={2}>
              {gender || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="年龄" span={2}>
              {age || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="身高" span={2}>
              {height ? `${height}cm` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="体重" span={2}>
              {weight ? `${weight}kg` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="BMI" span={2}>
              {bmi || calculateBMI(height, weight)}{' '}
              <Tooltip
                title={
                  <>
                    <p>过轻：BMI＜18.5</p>
                    <p>正常：18.5≤BMI＜24</p>
                    <p>超重：24≤BMI＜28</p>
                    <p>肥胖：28≤BMI</p>
                  </>
                }
              >
                {' '}
                <QuestionCircleTwoTone />
              </Tooltip>
            </Descriptions.Item>
            <Descriptions.Item label="基础代谢" span={2}>
              {bmr || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="地理位置" span={2}>
              {geoInfo
                ? `${geoInfo.territory || ''}-${geoInfo.region || ''}-${
                    geoInfo.city || ''
                  }`
                : '-'}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title="付费订单" style={{ marginBottom: 16 }}>
          <Descriptions column={{ xxl: 3, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}>
            <Descriptions.Item label="订单总数">
              {userProfileData?.paidOrders?.totalCount || 0}
            </Descriptions.Item>
            <Descriptions.Item label="平均客单价">
              {userProfileData?.paidOrders?.totalCount > 0
                ? `¥${(
                    userProfileData.paidOrders.totalAmount /
                    userProfileData.paidOrders.totalCount
                  ).toFixed(2)}`
                : '¥0'}
            </Descriptions.Item>
            <Descriptions.Item label="总消费金额">
              {userProfileData?.paidOrders?.totalAmount
                ? `¥${userProfileData.paidOrders.totalAmount}`
                : '¥0'}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title="训练数据" style={{ marginBottom: 16 }}>
          <Descriptions column={{ xxl: 3, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}>
            <Descriptions.Item label="总训练次数">
              {userProfileData?.training?.totalTimes || 0}
            </Descriptions.Item>
            <Descriptions.Item label="总训练时长">
              {userProfileData?.training?.totalMinutes
                ? `${userProfileData.training.totalMinutes}分钟`
                : '0分钟'}
            </Descriptions.Item>
            <Descriptions.Item label="平均单次时长">
              {userProfileData?.training?.totalTimes &&
              userProfileData?.training?.totalMinutes
                ? `${Math.round(
                    userProfileData.training.totalMinutes /
                      userProfileData.training.totalTimes,
                  )}分钟`
                : '0分钟'}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {questionnaireAnswers && questionnaireAnswers.length > 0 && (
          <Card title="问卷结果" style={{ marginBottom: 16 }}>
            <Descriptions
              column={{ xxl: 3, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}
            >
              {questionnaireAnswers.map((item, index) => (
                <Descriptions.Item label={item.key} key={index}>
                  {item.type === 'value' ? (
                    item.value
                  ) : (
                    <Space>
                      {item.files?.map((file, fIndex) => {
                        if (file.type === 'image') {
                          return (
                            <Image
                              width={50}
                              src={file.url}
                              key={fIndex}
                              preview={true}
                            />
                          );
                        } else if (file.type === 'pdf') {
                          return (
                            <a
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              key={fIndex}
                              title="点击预览PDF"
                            >
                              <FilePdfOutlined
                                style={{ fontSize: '24px', color: '#ff4d4f' }}
                              />
                            </a>
                          );
                        }
                        return null;
                      })}
                    </Space>
                  )}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </Card>
        )}
      </>
    );
  };

  return (
    <div>
      {/* 采用老版 Tabs API：新版 items 写法选项卡会复用上次的选中结果 */}
      <Tabs onChange={callback}>
        {tabSource.map((e) => {
          return (
            <TabPane tab={e.relationName} key={e.relationId}>
              {/* 客户信息 */}
              {e.relationId === 0 ? baseContent() : baseRelationContent()}
              {/* 注意：TAB 顺序已调整！！！！！！！！！ */}
              <Tabs
                activeKey={currentTabsKey}
                defaultActiveKey="0"
                onChange={(key) => {
                  onTabChange(key);
                  // 当切换到群数据分析tab时自动加载数据
                  if (key === '14' && !groupAnalysisData) {
                    fetchGroupAnalysisData();
                  }
                  if (key === '17' && !userMemoryMapCoach && !memoryLoading) {
                    setMemoryLoading(true);
                    getUserMemory({
                      requestData: { userId: query.id, type: 1 },
                    })
                      .then(({ code, data }) => {
                        if (code === '00000') {
                          const map = data?.userMemoryModelMap || {};
                          setUserMemoryMapCoach(map);
                        }
                      })
                      .catch(() => {})
                      .finally(() => setMemoryLoading(false));
                  }
                  if (key === '16' && !userMemoryMapUser && !memoryLoading) {
                    setMemoryLoading(true);
                    getUserMemory({
                      requestData: { userId: query.id, type: 0 },
                    })
                      .then(({ code, data }) => {
                        if (code === '00000') {
                          const map = data?.userMemoryModelMap || {};
                          setUserMemoryMapUser(map);
                        }
                      })
                      .catch(() => {})
                      .finally(() => setMemoryLoading(false));
                  }
                  if (key === '16' && !memorySource && !memorySourceLoading) {
                    setMemorySourceLoading(true);
                    getUserMemoryData({
                      requestData: {
                        userId: currentRelationInfo?.userId || query.id,
                      },
                    })
                      .then(({ code, data }) => {
                        if (code === '00000') {
                          setMemorySource(data || {});
                        }
                      })
                      .catch(() => {})
                      .finally(() => setMemorySourceLoading(false));
                  }
                }}
              >
                {e.relationId === 0 ? (
                  <TabPane tab="概览" key="0">
                    <OverView userId={query.id} step={step.current} />
                  </TabPane>
                ) : (
                  ''
                )}

                {/* 基础资料 tab，使用 Descriptions 展示用户详细信息 */}
                <TabPane tab="基础资料" key="1">
                  {renderUserProfileDetails()}{' '}
                  {/* 调用渲染函数，支持主用户和亲属 */}
                </TabPane>
                {e.relationId === 0 ? (
                  <TabPane tab="订单记录" key="7">
                    <OrderRecordView
                      userId={query.id}
                      userPhone={query.phone}
                    />
                  </TabPane>
                ) : (
                  ''
                )}
                {e.relationId === 0 ? (
                  <TabPane tab="关联记录" key="5">
                    <RelativeView
                      userPhone={query.phone}
                      userId={query.id}
                      ref={relativeViewRef}
                    />
                  </TabPane>
                ) : (
                  ''
                )}

                {e.relationId === 0 ? (
                  // tabKey=11 外部要使用 ！！！
                  <TabPane tab="用户计划" key="11">
                    <UserPlanView phone={query.phone} userId={query.id} />
                  </TabPane>
                ) : (
                  ''
                )}

                <TabPane tab="上课课表" key="3">
                  <ClassList
                    ref={classListRef}
                    userPhoneInit={currentRelationPhone}
                    userIdInit={currentRelationInfo?.userId}
                    userRecordInit={true}
                    sort="desc"
                  />
                </TabPane>
                {e.relationId === 0 ? (
                  <TabPane tab="销课记录" key="6">
                    <DelClassRecordView
                      userId={query.id}
                      userPhone={query.phone}
                    />
                  </TabPane>
                ) : (
                  ''
                )}
                {e.relationId === 0 ? (
                  <TabPane tab="权益信息" key="4">
                    <RightView
                      userId={query.id}
                      userPhone={query.phone}
                      relatives={relatives}
                      callback={() => {
                        // 刷新关系表
                        relativeViewRef?.relativeTableRef?.current?.reload();
                      }}
                    />
                  </TabPane>
                ) : (
                  ''
                )}

                {e.relationId === 0 ? (
                  <TabPane tab="触达记录" key="10">
                    <TouchRecordView userId={query.id} />
                  </TabPane>
                ) : (
                  ''
                )}

                {e.relationId === 0 ? (
                  <TabPane tab="行为路径" key="8">
                    <PathRecordView userId={query.id} />
                  </TabPane>
                ) : (
                  ''
                )}

                {e.relationId === 0 ? (
                  <TabPane tab="会话记录" key="9">
                    <ChatListView userId={query.id} />
                  </TabPane>
                ) : (
                  ''
                )}
                {e.relationId === 0 ? (
                  <TabPane tab="跟进记录" key="2">
                    {continueList()}
                  </TabPane>
                ) : (
                  ''
                )}
                {e.relationId === 0 && (
                  <TabPane tab="LITTA点播" key="12">
                    <LittaVodRecordView userId={query.id} />
                  </TabPane>
                )}
                {e.relationId === 0 && (
                  <TabPane tab="打卡记录" key="13">
                    <TrainingView userId={query.id} />
                  </TabPane>
                )}

                {e.relationId === 0 && (
                  <TabPane tab="群数据分析" key="14">
                    <div style={{ padding: '16px' }}>
                      <div
                        style={{
                          marginBottom: '16px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Title level={4}>
                          数据时间范围
                          {groupAnalysisData?.data?.[0]?.startTime &&
                            groupAnalysisData?.data?.[0]?.endTime && (
                              <span
                                style={{
                                  fontSize: '14px',
                                  fontWeight: 'normal',
                                  color: '#666',
                                  marginLeft: '8px',
                                }}
                              >
                                ({groupAnalysisData.data[0].startTime} -{' '}
                                {groupAnalysisData.data[0].endTime})
                              </span>
                            )}
                        </Title>
                      </div>

                      {groupAnalysisLoading && (
                        <Card>
                          <div style={{ textAlign: 'center', padding: '40px' }}>
                            <span>正在获取群数据分析...</span>
                          </div>
                        </Card>
                      )}

                      {!groupAnalysisLoading && !groupAnalysisData && (
                        <Card>
                          <div style={{ textAlign: 'center', padding: '40px' }}>
                            <span style={{ color: '#999' }}>
                              暂无群数据分析数据
                            </span>
                          </div>
                        </Card>
                      )}

                      {!groupAnalysisLoading && groupAnalysisData && (
                        <>
                          {/* 用户行为展示 */}
                          <Card
                            title="用户行为"
                            style={{ marginBottom: '24px' }}
                          >
                            {(() => {
                              try {
                                const currentDataItem =
                                  groupAnalysisData?.data?.[0];
                                if (!currentDataItem) {
                                  return (
                                    <Empty description="暂无用户行为数据" />
                                  );
                                }

                                const behaviorData =
                                  typeof currentDataItem.eventData === 'string'
                                    ? JSON.parse(currentDataItem.eventData)
                                    : currentDataItem.eventData;

                                return behaviorData?.length > 0 ? (
                                  <Row gutter={[16, 16]}>
                                    {behaviorData.map((behavior, index) => (
                                      <Col span={12} key={index}>
                                        <Card
                                          size="small"
                                          style={{ background: '#f8f9fa' }}
                                        >
                                          <div
                                            style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'space-between',
                                              gap: '8px',
                                            }}
                                          >
                                            <div
                                              style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                              }}
                                            >
                                              <Tag
                                                color={
                                                  behavior[0] === '用户'
                                                    ? 'blue'
                                                    : 'green'
                                                }
                                              >
                                                {behavior[0]}
                                              </Tag>
                                              <span
                                                style={{
                                                  color: '#666',
                                                  fontWeight: 'bold',
                                                }}
                                              >
                                                {behavior[1]}
                                              </span>
                                              <Tag color="orange">
                                                {behavior[2]}
                                              </Tag>
                                            </div>
                                            <span
                                              style={{
                                                fontSize: '12px',
                                                color: '#999',
                                              }}
                                            >
                                              {behavior[3]}
                                            </span>
                                          </div>
                                        </Card>
                                      </Col>
                                    ))}
                                  </Row>
                                ) : (
                                  <Empty description="暂无用户行为数据" />
                                );
                              } catch (error) {
                                console.error('解析用户行为数据失败:', error);
                                return <Empty description="数据格式错误" />;
                              }
                            })()}
                          </Card>

                          {/* 行为描述展示 */}
                          <Card title="行为描述">
                            {(() => {
                              try {
                                const currentDataItem =
                                  groupAnalysisData?.data?.[0];
                                if (!currentDataItem) {
                                  return (
                                    <Empty description="暂无行为描述数据" />
                                  );
                                }

                                const descriptionData =
                                  typeof currentDataItem.eventDescription ===
                                  'string'
                                    ? JSON.parse(
                                        currentDataItem.eventDescription,
                                      )
                                    : currentDataItem.eventDescription;

                                return descriptionData?.length > 0 ? (
                                  <Row gutter={[16, 16]}>
                                    {descriptionData.map((event, index) => (
                                      <Col span={12} key={index}>
                                        <Card
                                          size="small"
                                          title={
                                            <div
                                              style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                              }}
                                            >
                                              <Tag
                                                color={
                                                  {
                                                    体测更新: 'blue',
                                                    课程参与: 'green',
                                                    服务评估: 'orange',
                                                    训练指导: 'purple',
                                                    健康建议: 'cyan',
                                                    普拉提指导: 'magenta',
                                                  }[event.类型] || 'default'
                                                }
                                              >
                                                {event.类型}
                                              </Tag>
                                              <span
                                                style={{
                                                  fontSize: '12px',
                                                  color: '#999',
                                                }}
                                              >
                                                {event.时间}
                                              </span>
                                            </div>
                                          }
                                          style={{ marginBottom: '16px' }}
                                        >
                                          <div style={{ lineHeight: '1.6' }}>
                                            {Object.entries(event).map(
                                              ([key, value]) => {
                                                if (
                                                  key === '类型' ||
                                                  key === '时间'
                                                )
                                                  return null;
                                                return (
                                                  <div
                                                    key={key}
                                                    style={{
                                                      marginBottom: '8px',
                                                    }}
                                                  >
                                                    <span
                                                      style={{
                                                        fontWeight: 'bold',
                                                        color: '#333',
                                                      }}
                                                    >
                                                      {key}：
                                                    </span>
                                                    {Array.isArray(value) ? (
                                                      <span>
                                                        {value.map(
                                                          (item, idx) => (
                                                            <Tag
                                                              key={idx}
                                                              size="small"
                                                              style={{
                                                                margin:
                                                                  '0 4px 0 0',
                                                              }}
                                                            >
                                                              {item}
                                                            </Tag>
                                                          ),
                                                        )}
                                                      </span>
                                                    ) : typeof value ===
                                                      'object' ? (
                                                      <span
                                                        style={{
                                                          color: '#666',
                                                        }}
                                                      >
                                                        {Object.entries(value)
                                                          .map(
                                                            ([k, v]) =>
                                                              `${k}: ${v}`,
                                                          )
                                                          .join(', ')}
                                                      </span>
                                                    ) : (
                                                      <span
                                                        style={{
                                                          color: '#666',
                                                        }}
                                                      >
                                                        {value}
                                                      </span>
                                                    )}
                                                  </div>
                                                );
                                              },
                                            )}
                                          </div>
                                        </Card>
                                      </Col>
                                    ))}
                                  </Row>
                                ) : (
                                  <Empty description="暂无行为描述数据" />
                                );
                              } catch (error) {
                                console.error('解析行为描述数据失败:', error);
                                return <Empty description="数据格式错误" />;
                              }
                            })()}
                          </Card>

                          {/* 分页组件 */}
                          {!groupAnalysisLoading &&
                            groupAnalysisData?.page &&
                            groupAnalysisData.page.pages > 1 && (
                              <div
                                style={{
                                  textAlign: 'center',
                                  marginTop: '24px',
                                }}
                              >
                                <Pagination
                                  current={groupAnalysisCurrentPage}
                                  total={groupAnalysisData.page.count}
                                  pageSize={groupAnalysisPageSize}
                                  onChange={(page, pageSize) => {
                                    setGroupAnalysisCurrentPage(page);
                                    setGroupAnalysisPageSize(pageSize);
                                    fetchGroupAnalysisData(page, pageSize);
                                  }}
                                  showSizeChanger={false}
                                  showQuickJumper
                                  showTotal={(total, range) =>
                                    `第 ${range[0]} 条，共 ${total} 条`
                                  }
                                />
                              </div>
                            )}
                        </>
                      )}
                    </div>
                  </TabPane>
                )}
                {e.relationId === 0 && (
                  <TabPane tab="健康习惯" key="15">
                    <HealthHabitView userId={query.id} />
                  </TabPane>
                )}
                {e.relationId === 0 && (
                  <TabPane tab="记忆体" key="16">
                    <div style={{ padding: 16 }}>
                      <Space
                        direction="vertical"
                        style={{ width: '100%' }}
                        size="large"
                      >
                        <Card
                          title="记忆体源数据"
                          headStyle={{ background: '#f0f7ff' }}
                        >
                          {(() => {
                            const src = memorySource || {};
                            const hasData = Object.keys(src).length > 0;
                            if (!hasData) {
                              return (
                                <div
                                  style={{ textAlign: 'center', color: '#999' }}
                                >
                                  {memorySourceLoading
                                    ? '正在加载...'
                                    : '暂无数据'}
                                </div>
                              );
                            }
                            const profile = src.memoryUserProfile || null;
                            const activities = Array.isArray(
                              src.memoryDailyActivityList,
                            )
                              ? src.memoryDailyActivityList
                              : [];
                            const interactions = Array.isArray(
                              src.memoryUserInteractionList,
                            )
                              ? src.memoryUserInteractionList
                              : [];
                            const rights = Array.isArray(
                              src.memoryUserRightsList,
                            )
                              ? src.memoryUserRightsList
                              : [];
                            const midInteractions = Array.isArray(
                              src.memoryUserMidInteractionList,
                            )
                              ? src.memoryUserMidInteractionList
                              : [];
                            const midActivities = Array.isArray(
                              src.memoryMidDailyActivityList,
                            )
                              ? src.memoryMidDailyActivityList
                              : [];

                            return (
                              <Space
                                direction="vertical"
                                style={{ width: '100%' }}
                                size="large"
                              >
                                {profile ? (
                                  <Card size="small" title="用户画像源数据">
                                    <Space
                                      direction="vertical"
                                      style={{ width: '100%' }}
                                      size="small"
                                    >
                                      <div>姓名：{profile.name || '-'}</div>
                                      <div>性别：{profile.gender || '-'}</div>
                                      <div>
                                        生日：{profile.birthDate || '-'}
                                      </div>
                                      <div>身高：{profile.height || '-'}</div>
                                      <div>
                                        初始体重：{profile.initialWeight || '-'}
                                      </div>
                                      <div>
                                        当前BMI：{profile.currentBmi || '-'}
                                      </div>
                                      <div>
                                        体脂率：{profile.bodyFatRate || '-'}
                                      </div>
                                      <div>
                                        运动类型：{profile.sportType || '-'}
                                      </div>
                                      {(() => {
                                        let health = null;
                                        try {
                                          health = profile.healthStatusJson
                                            ? JSON.parse(
                                                profile.healthStatusJson,
                                              )
                                            : null;
                                        } catch {}
                                        return health ? (
                                          <div>
                                            健康状态：
                                            {Object.entries(health)
                                              .map(([k, v]) => `${k}:${v}`)
                                              .join('，')}
                                          </div>
                                        ) : null;
                                      })()}
                                      {(() => {
                                        let goal = null;
                                        try {
                                          goal = profile.fitnessGoal
                                            ? JSON.parse(profile.fitnessGoal)
                                            : null;
                                        } catch {}
                                        return goal ? (
                                          <div>
                                            健身目标：
                                            {Object.entries(goal)
                                              .map(([k, v]) => `${k}:${v}`)
                                              .join('，')}
                                          </div>
                                        ) : null;
                                      })()}
                                      {(() => {
                                        let diet = null;
                                        try {
                                          diet = profile.dietPreferenceJson
                                            ? JSON.parse(
                                                profile.dietPreferenceJson,
                                              )
                                            : null;
                                        } catch {}
                                        return diet ? (
                                          <div>
                                            饮食偏好：
                                            {Object.entries(diet)
                                              .map(([k, v]) => `${k}:${v}`)
                                              .join('，')}
                                          </div>
                                        ) : null;
                                      })()}
                                      <div>
                                        每周频次：
                                        {profile.frequencyPerWeek || '-'}
                                      </div>
                                    </Space>
                                  </Card>
                                ) : null}

                                <Card size="small" title="活动源数据">
                                  {activities.length === 0 ? (
                                    <div style={{ color: '#999' }}>
                                      暂无数据
                                    </div>
                                  ) : (
                                    <Space
                                      direction="vertical"
                                      style={{ width: '100%' }}
                                      size="small"
                                    >
                                      {activities.map((a, idx) => {
                                        const isDiet = String(
                                          a?.busIdent || '',
                                        ).startsWith('diet_');
                                        let parsedDetails;
                                        try {
                                          parsedDetails =
                                            typeof a?.dataDetails === 'string'
                                              ? JSON.parse(a.dataDetails)
                                              : a?.dataDetails;
                                        } catch {}
                                        const formatDetails = () => {
                                          if (
                                            !parsedDetails &&
                                            typeof a?.dataDetails === 'string'
                                          )
                                            return a.dataDetails;
                                          if (Array.isArray(parsedDetails))
                                            return parsedDetails.join('，');
                                          if (
                                            parsedDetails &&
                                            typeof parsedDetails === 'object'
                                          )
                                            return Object.entries(parsedDetails)
                                              .map(([k, v]) => `${k}:${v}`)
                                              .join('，');
                                          return '';
                                        };
                                        return (
                                          <div
                                            key={idx}
                                            style={{ lineHeight: 1.6 }}
                                          >
                                            <Space wrap>
                                              {a?.startTime ? (
                                                <Tag color="gold">
                                                  {moment(a.startTime).isValid()
                                                    ? moment(
                                                        a.startTime,
                                                      ).format(
                                                        'YYYY-MM-DD HH:mm',
                                                      )
                                                    : a.startTime}
                                                </Tag>
                                              ) : null}
                                              <Tag color="blue">
                                                {a.activityType || '-'}
                                              </Tag>
                                              {!isDiet ? (
                                                <Tag color="cyan">
                                                  {String(
                                                    a.durationMinutes || '-',
                                                  ) + '分钟'}
                                                </Tag>
                                              ) : null}
                                              <Tag color="purple">
                                                {String(
                                                  a.caloriesChange || '-',
                                                ) + '千卡'}
                                              </Tag>
                                              {a?.locationType ? (
                                                <Tag color="geekblue">
                                                  {a.locationType}
                                                </Tag>
                                              ) : null}
                                              {(() => {
                                                const summary = (
                                                  a?.contentSummary || ''
                                                ).trim();
                                                return summary ? (
                                                  <Tag color="orange">
                                                    {summary}
                                                  </Tag>
                                                ) : null;
                                              })()}
                                              {(() => {
                                                const details = formatDetails();
                                                return details ? (
                                                  <Tag color="magenta">
                                                    {details}
                                                  </Tag>
                                                ) : null;
                                              })()}
                                            </Space>
                                          </div>
                                        );
                                      })}
                                    </Space>
                                  )}
                                </Card>

                                <Card size="small" title="交互源数据">
                                  {interactions.length === 0 ? (
                                    <div style={{ color: '#999' }}>
                                      暂无数据
                                    </div>
                                  ) : (
                                    <Space
                                      direction="vertical"
                                      style={{ width: '100%' }}
                                      size="small"
                                    >
                                      {interactions.map((it, idx) => {
                                        const t = it?.type || '-';
                                        const isChat = t === '群聊天';
                                        const isLesson = t === '真人课';
                                        let detailsArray = [];
                                        let detailsString = '';
                                        try {
                                          if (isLesson) {
                                            const parsed =
                                              typeof it?.dataDetails ===
                                              'string'
                                                ? JSON.parse(it.dataDetails)
                                                : it?.dataDetails;
                                            if (Array.isArray(parsed))
                                              detailsArray = parsed;
                                          } else {
                                            detailsString =
                                              typeof it?.dataDetails ===
                                              'string'
                                                ? it.dataDetails
                                                : Array.isArray(it?.dataDetails)
                                                ? it.dataDetails.join('，')
                                                : '';
                                          }
                                        } catch {}
                                        return (
                                          <div
                                            key={idx}
                                            style={{ lineHeight: 1.6 }}
                                          >
                                            <Space wrap>
                                              {it?.startTime ? (
                                                <Tag color="gold">
                                                  {moment(
                                                    it.startTime,
                                                  ).isValid()
                                                    ? moment(
                                                        it.startTime,
                                                      ).format(
                                                        'YYYY-MM-DD HH:mm',
                                                      )
                                                    : it.startTime}
                                                </Tag>
                                              ) : null}
                                              <Tag color="green">{t}</Tag>
                                            </Space>
                                            {(isChat && detailsString) ||
                                            (isLesson &&
                                              detailsArray.length) ? (
                                              <div style={{ marginTop: 6 }}>
                                                {isChat && detailsString ? (
                                                  <Space
                                                    wrap
                                                    style={{ width: '100%' }}
                                                  >
                                                    {detailsString
                                                      .split(/\r?\n\s*\r?\n+/)
                                                      .map((seg, i) => {
                                                        const s = (
                                                          seg || ''
                                                        ).trim();
                                                        return s ? (
                                                          <Tag
                                                            color="magenta"
                                                            key={i}
                                                            style={{
                                                              whiteSpace:
                                                                'normal',
                                                              wordBreak:
                                                                'break-word',
                                                              maxWidth: '100%',
                                                            }}
                                                          >
                                                            {s}
                                                          </Tag>
                                                        ) : null;
                                                      })}
                                                  </Space>
                                                ) : null}
                                                {isLesson &&
                                                detailsArray.length ? (
                                                  <Space
                                                    wrap
                                                    style={{ width: '100%' }}
                                                  >
                                                    {detailsArray.map(
                                                      (s, k) => (
                                                        <Tag
                                                          color="magenta"
                                                          key={k}
                                                          style={{
                                                            whiteSpace:
                                                              'normal',
                                                            wordBreak:
                                                              'break-word',
                                                            maxWidth: '100%',
                                                          }}
                                                        >
                                                          {s}
                                                        </Tag>
                                                      ),
                                                    )}
                                                  </Space>
                                                ) : null}
                                              </div>
                                            ) : null}
                                          </div>
                                        );
                                      })}
                                    </Space>
                                  )}
                                </Card>

                                <MidMemoryCard
                                  title="中期记忆交互"
                                  list={midInteractions}
                                />

                                <MidMemoryCard
                                  title="中期记忆活动"
                                  list={midActivities}
                                />

                                <Card size="small" title="权益源数据">
                                  {rights.length === 0 ? (
                                    <div style={{ color: '#999' }}>
                                      暂无数据
                                    </div>
                                  ) : (
                                    <Space
                                      direction="vertical"
                                      style={{ width: '100%' }}
                                      size="small"
                                    >
                                      {(() => {
                                        const details = rights.filter(
                                          (r) =>
                                            String(
                                              r?.dimensionType,
                                            ).toUpperCase() === 'DETAIL',
                                        );
                                        const renderRight = (r, i) => {
                                          let transInfo;
                                          try {
                                            const parsed =
                                              typeof r?.transactionRecordInfo ===
                                              'string'
                                                ? JSON.parse(
                                                    r.transactionRecordInfo,
                                                  )
                                                : r?.transactionRecordInfo;
                                            if (
                                              parsed &&
                                              typeof parsed === 'object'
                                            ) {
                                              transInfo = Object.entries(parsed)
                                                .map(([k, v]) => `${k}:${v}`)
                                                .join('，');
                                            } else if (Array.isArray(parsed)) {
                                              transInfo = parsed.join('，');
                                            } else {
                                              transInfo =
                                                r?.transactionRecordInfo || '';
                                            }
                                          } catch {
                                            transInfo =
                                              r?.transactionRecordInfo || '';
                                          }
                                          const range = `${
                                            r?.startDate || '-'
                                          } ~ ${r?.endDate || '-'}`;
                                          return (
                                            <div
                                              key={i}
                                              style={{ lineHeight: 1.6 }}
                                            >
                                              <Space wrap>
                                                <Tag color="gold">{range}</Tag>
                                                <Tag color="blue">
                                                  {r?.exerciseType || '-'}
                                                </Tag>
                                                <Tag color="orange">
                                                  {r?.status || '-'}
                                                </Tag>
                                                {transInfo ? (
                                                  <Tag color="magenta">
                                                    {transInfo}
                                                  </Tag>
                                                ) : null}
                                              </Space>
                                            </div>
                                          );
                                        };
                                        return details.length === 0 ? (
                                          <div style={{ color: '#999' }}>
                                            暂无数据
                                          </div>
                                        ) : (
                                          <Space
                                            direction="vertical"
                                            style={{ width: '100%' }}
                                            size="small"
                                          >
                                            {details.map((r, idx) =>
                                              renderRight(r, `d-${idx}`),
                                            )}
                                          </Space>
                                        );
                                      })()}
                                    </Space>
                                  )}
                                </Card>
                              </Space>
                            );
                          })()}
                        </Card>
                        <MemoryMapCard
                          map={userMemoryMapUser}
                          loading={memoryLoading}
                        />
                      </Space>
                    </div>
                  </TabPane>
                )}
                {e.relationId === 0 && (
                  <TabPane tab="服务者记忆体" key="17">
                    <div style={{ padding: 16 }}>
                      <Space
                        direction="vertical"
                        style={{ width: '100%' }}
                        size="large"
                      >
                        <MemoryMapCard
                          map={userMemoryMapCoach}
                          loading={memoryLoading}
                        />
                      </Space>
                    </div>
                  </TabPane>
                )}
              </Tabs>
            </TabPane>
          );
        })}
      </Tabs>

      {addFamilyModalView()}
      {addFollowModalView()}
      {addFeatureModalView()}
      {stopModalView()}
      {wxBindModalView()}
      {autoSignModalView()}
      {changePhoneModalView()}
    </div>
  );
};
export default record;
